import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import { useSelector } from "react-redux";
import { getSidebarMenuForUser } from "../../data/sidebarMenu";
import { selectAuth } from "../../redux/auth/authSlice";
import { getRoleBadgeLabel } from "../../utils/userRole";

function Sidebar() {
  const location = useLocation();
  const { user } = useSelector(selectAuth);
  const menuItems = useMemo(() => getSidebarMenuForUser(user), [user]);

  const [expandedGroups, setExpandedGroups] = useState({});

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    "?";

  useEffect(() => {
    const nextExpanded = {};
    menuItems.forEach((item) => {
      if (!item.children) return;
      const activeInGroup = item.children.some((child) =>
        location.pathname.startsWith(child.to),
      );
      nextExpanded[item.label] = activeInGroup;
    });
    setExpandedGroups((prev) => ({ ...nextExpanded, ...prev }));
  }, [location.pathname, menuItems]);

  const toggleGroup = (groupLabel) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  const roleBadge = getRoleBadgeLabel(user);

  return (
    <aside className="sidebar-shell flex h-full min-h-screen w-[210px] shrink-0 flex-col border-r border-base-300/80 bg-base-100 shadow-[4px_0_24px_-12px_rgba(0,0,0,0.06)]">
      <div className="border-b border-base-300/80 px-2 py-3">
        <Link
          to="/"
          className="flex items-center gap-2.5 rounded-xl outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <div
            className="grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary to-primary/80 text-base font-bold tracking-tight text-primary-content shadow-sm"
            aria-hidden
          >
            B
          </div>
          <p className="ui-brand-title truncate text-sm leading-tight">Builders</p>
        </Link>
      </div>

      <nav
        aria-label="Main navigation"
        className="sidebar-nav flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-1.5 pb-3 pt-2"
      >
        <ul className="flex w-full flex-col gap-1 p-0">
          {menuItems.map((item) => {
            const Icon = item.icon;

            if (item.children) {
              const hasActiveChild = item.children.some(
                (child) => location.pathname === child.to,
              );
              const isGroupOpen = expandedGroups[item.label];
              const isParentActive = item.to
                ? location.pathname.startsWith(item.to)
                : hasActiveChild;

              return (
                <li key={item.label} className="w-full list-none">
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.label)}
                    aria-label={`Toggle ${item.label} submenu`}
                    className={[
                      "sidebar-parent flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors",
                      isParentActive
                        ? "bg-primary/12 font-semibold text-primary"
                        : "font-medium text-base-content hover:bg-base-200/80",
                    ].join(" ")}
                  >
                    <Icon
                      className={[
                        "h-4 w-4 shrink-0",
                        isParentActive
                          ? "text-primary"
                          : "text-base-content/70",
                      ].join(" ")}
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <span className="flex-1 truncate ui-nav-text">{item.label}</span>
                    <FiChevronDown
                      className={[
                        "h-4 w-4 shrink-0 text-current/50 transition-transform duration-200",
                        isGroupOpen ? "rotate-180" : "",
                      ].join(" ")}
                      aria-hidden
                    />
                  </button>

                  {isGroupOpen ? (
                    <ul className="mt-1 space-y-0.5 border-l-2 border-base-300/90 pl-2.5">
                      {item.children.map((child) => (
                        <li key={child.to} className="list-none">
                          <NavLink
                            to={child.to}
                            className={({ isActive }) =>
                              [
                                "sidebar-sublink relative block rounded-lg py-2 pl-2.5 pr-1.5 ui-nav-text transition-colors",
                                isActive
                                  ? "sidebar-sublink-active bg-primary/15 font-semibold text-primary"
                                  : "font-normal text-base-content/75 hover:bg-base-200/70 hover:text-base-content",
                              ].join(" ")
                            }
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            }

            return (
              <li key={item.to} className="w-full list-none">
                <NavLink to={item.to} end={item.to === "/"}>
                  {({ isActive }) => (
                    <span
                      className={[
                        "sidebar-toplink relative flex items-center gap-2 rounded-lg px-2 py-2 ui-nav-text transition-colors",
                        isActive
                          ? "sidebar-toplink-active bg-primary/12 font-semibold text-primary shadow-sm"
                          : "font-medium text-base-content hover:bg-base-200/70",
                      ].join(" ")}
                    >
                      <Icon
                        className={[
                          "h-4 w-4 shrink-0 transition-colors",
                          isActive
                            ? "text-primary"
                            : "text-base-content opacity-85",
                        ].join(" ")}
                        strokeWidth={1.75}
                        aria-hidden
                      />
                      <span className="truncate">{item.label}</span>
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}

export default Sidebar;
